/**
 * What each role sees first.
 *
 * The same order drives the desktop header and the mobile tab bar, so a person
 * moving between their laptop and their phone finds things in the same places.
 *
 * Order is by role because the roles do different jobs: marketing lives in the
 * lead pipeline, a developer in tasks and tickets, a client in their project.
 * Putting the dashboard first for everyone and then whatever that person opens
 * most is the whole rule.
 *
 * A destination absent from a role's list still appears, after the listed ones,
 * as long as that role can reach it -- so adding a resource cannot silently
 * hide it from anyone.
 */
export const NAVIGATION_ORDER: Record<string, string[]> = {
  // Marketing works the pipeline: the lead, then the person, then the
  // follow-up, then the company they belong to.
  marketing: ["/", "/deals", "/contacts", "/tasks", "/companies"],

  // A developer's day is the work assigned to them and what is being reported.
  developer: ["/", "/tasks", "/tickets", "/projects"],

  // A client has one question -- how is my project -- and one way to ask about
  // it.
  client: ["/", "/projects", "/tickets"],

  // Collaborators run projects and bring in leads.
  collaborator: ["/", "/projects", "/tasks", "/deals", "/contacts"],

  // An admin has everything, so the order is the shape of the business rather
  // than one job inside it.
  admin: [
    "/",
    "/deals",
    "/projects",
    "/tasks",
    "/tickets",
    "/contacts",
    "/companies",
  ],
};

/**
 * Which arrangement a person gets, worked out from what they can do.
 *
 * Roles themselves are not readable by the people who hold them -- user_roles
 * is behind users.manage_roles, so only an admin can query it -- while
 * permissions are available to everyone through my_permissions. Since a role is
 * only a bundle of permissions anyway, the permissions answer the same question
 * without a query nobody but an admin is allowed to make.
 *
 * Ordered most specific first: an admin also passes the marketing test.
 */
export function profileFor(can: (permission: string) => boolean): string {
  if (can("users.list")) return "admin";
  if (can("projects.assign")) return "collaborator";
  if (can("leads.create")) return "marketing";
  if (can("tasks.list") && !can("contacts.list")) return "developer";
  if (can("projects.list")) return "client";
  return "default";
}

/** Used when someone holds no role that names an order, or holds several. */
export const DEFAULT_ORDER = [
  "/",
  "/deals",
  "/projects",
  "/tasks",
  "/tickets",
  "/contacts",
  "/companies",
];

/**
 * Sort destinations into the arrangement for a profile.
 *
 * Stable: anything the arrangement does not name keeps its own relative order
 * after the ones it does, so adding a resource cannot silently hide it.
 */
export function orderFor<T extends { href: string }>(
  items: T[],
  profile: string,
): T[] {
  const order = NAVIGATION_ORDER[profile] ?? DEFAULT_ORDER;

  const rank = (href: string) => {
    const index = order.indexOf(href);
    // Anything unlisted sorts after everything listed, keeping its own relative
    // order rather than jumping to the front.
    return index === -1 ? order.length : index;
  };

  return [...items].sort((a, b) => rank(a.href) - rank(b.href));
}
